import {
  type ChangeEvent,
  type ClipboardEvent,
  type Dispatch,
  type FocusEvent,
  type KeyboardEvent,
  type SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useController } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  type LoginRegistrationOneTimePasswordInputFormData,
  OTP_LENGTH,
  validateOneTimePassword,
} from '../LoginRegistrationOneTimePasswordInputForm/loginRegistrationOneTimePasswordInputFormData'

interface OneTimePasswordInputHandlersParameters {
  focusInput: (index: number) => void
  focusNextInput: (index: number) => void
  isFirstFocus: boolean
  length: number
  onBlur: () => void
  onChange: (value: string) => void
  otp: string[]
  setIsFirstFocus: Dispatch<SetStateAction<boolean>>
  setOtp: Dispatch<SetStateAction<string[]>>
  setShowError: (showError: boolean) => void
}

interface OneTimePasswordInputHandlers {
  handleBlur: (event: FocusEvent<HTMLDivElement>) => void
  handleChange: (event: ChangeEvent<HTMLInputElement>, index: number) => void
  handleFocus: () => void
  handleKeyDown: (event: KeyboardEvent<HTMLInputElement>, index: number) => void
  handlePaste: (event: ClipboardEvent<HTMLInputElement>) => void
}

interface LoginRegistrationOneTimePasswordInputState extends OneTimePasswordInputHandlers {
  handleInputRef: (element: HTMLInputElement | null, index: number) => void
  inputKeys: string[]
  otp: string[]
  showError: boolean
}

const isKeyNumber = (key: string): boolean =>
  !Number.isNaN(Math.trunc(Number(key)))

const useOneTimePasswordInputHandlers = ({
  focusInput,
  focusNextInput,
  isFirstFocus,
  length,
  onBlur,
  onChange,
  otp,
  setIsFirstFocus,
  setOtp,
  setShowError,
}: OneTimePasswordInputHandlersParameters): OneTimePasswordInputHandlers => {
  const handleFocus = (): void => {
    if (isFirstFocus && otp.join('') === '') {
      setIsFirstFocus(false)
      focusNextInput(-1)
    }
    setShowError(false)
  }

  const handleNumberKey = (key: string, index: number): void => {
    const newOtp = [...otp]
    newOtp[index] = key
    onChange(newOtp.join(''))
    setOtp(newOtp)
    focusNextInput(index)
    setShowError(false)
  }

  const handleBackspaceOrDelete = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ): void => {
    event.preventDefault()
    if (otp[index] === '') {
      if (index !== 0) {
        focusInput(index - 1)
      }
    } else {
      const newOtp = [...otp]
      newOtp[index] = ''
      onChange(newOtp.join(''))
      setOtp(newOtp)
      setTimeout(() => {
        focusInput(index)
      }, 0)
    }
    setShowError(false)
  }

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    index: number,
  ): void => {
    const { value } = event.target
    if (value && isKeyNumber(value)) {
      handleNumberKey(value, index)
    }
  }

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ): void => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      handleBackspaceOrDelete(event, index)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault()
    const pasteData = [...event.clipboardData.getData('text')]
      .filter((inputKey) => isKeyNumber(inputKey))
      .slice(0, length)

    const newOtp = Array.from(
      { length },
      (_value, index) => pasteData[index] || '',
    )
    setOtp(newOtp)
    onChange(newOtp.join(''))
    const nextIndex = pasteData.length - 1
    if (nextIndex < length) {
      focusNextInput(nextIndex)
    }
    setShowError(false)
  }

  const handleBlur = (event: FocusEvent<HTMLDivElement>): void => {
    if (event.currentTarget.contains(event.relatedTarget)) {
      return
    }

    onBlur()
    setShowError(true)
  }

  return {
    handleBlur,
    handleChange,
    handleFocus,
    handleKeyDown,
    handlePaste,
  }
}

export const useLoginRegistrationOneTimePasswordInput = (
  length: number,
): LoginRegistrationOneTimePasswordInputState => {
  const intl = useIntl()
  const {
    field,
    formState: { submitCount },
  } = useController<LoginRegistrationOneTimePasswordInputFormData>({
    name: 'password',
    rules: {
      validate: (value) =>
        validateOneTimePassword(
          value,
          intl.formatMessage({ id: 'form.error.input.missing' }),
          intl.formatMessage(
            { id: 'form.error.input.minLength' },
            { minLength: OTP_LENGTH },
          ),
        ),
    },
  })
  const [isFirstFocus, setIsFirstFocus] = useState(true)
  const [otp, setOtp] = useState<string[]>(() => {
    const initialOtp = [...field.value]

    return Array.from({ length }, (_value, index) => initialOtp[index] || '')
  })
  const inputs = useRef<(HTMLInputElement | null)[]>(
    Array.from({ length }, () => null),
  )
  const inputKeys = Array.from(
    { length },
    (_value, index) => `otp-input-${index + 1}`,
  )

  useEffect(() => {
    if (otp.join('') === '') {
      inputs.current[0]?.focus()
    }
  }, [otp])

  const [errorVisibility, setErrorVisibility] = useState({
    showError: false,
    submitCount,
  })
  const showError =
    errorVisibility.submitCount === submitCount
      ? errorVisibility.showError
      : submitCount > 0
  const setShowError = useCallback(
    (shouldShowError: boolean): void => {
      setErrorVisibility({ showError: shouldShowError, submitCount })
    },
    [submitCount],
  )

  const focusInput = useCallback((index: number): void => {
    inputs.current[index]?.focus()
  }, [])

  const focusNextInput = useCallback(
    (index: number) => {
      if (index + 1 < length) {
        inputs.current[index + 1]?.focus()
      } else if (index + 1 === length) {
        inputs.current[length - 1]?.focus()
      }
    },
    [length],
  )

  const handleInputRef = useCallback(
    (element: HTMLInputElement | null, index: number): void => {
      inputs.current[index] = element
      if (index === 0) {
        field.ref(element)
      }
    },
    [field],
  )

  const handlers = useOneTimePasswordInputHandlers({
    focusInput,
    focusNextInput,
    isFirstFocus,
    length,
    onBlur: field.onBlur,
    onChange: field.onChange,
    otp,
    setIsFirstFocus,
    setOtp,
    setShowError,
  })

  return { ...handlers, handleInputRef, inputKeys, otp, showError }
}
